import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DemoExplainer from '@/components/DemoExplainer';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Radio, LayoutGrid, Users, LogOut, Sparkles, BarChart2, Layers, LineChart, Shuffle, GitMerge } from 'lucide-react';

/* ================================================================
   TOKENS
   ================================================================ */
// Design tokens sourced from tokens.css — use var(--token) in JSX styles
// Colour aliases for JS references (mirrors tokens.css)
const C = {
  bg:       '#0A0A1A',
  bg2:      '#0D0D20',
  bg3:      'rgba(255,255,255,0.03)',
  bg4:      'rgba(255,255,255,0.06)',
  text:     '#FFFFFF',
  t2:       'rgba(255,255,255,0.92)',
  t3:       'rgba(255,255,255,0.72)',
  t4:       'rgba(255,255,255,0.48)',
  line:     'rgba(255,255,255,0.10)',
  lines:    'rgba(255,255,255,0.07)',
  bot:      '#00D4C8',
  borrow:   '#06B6D4',
  build:    '#C9A84C',
  bi:       '#E8B96A',
  buy:      '#EC4899',
  teal:     '#00D4C8',
  gold:     '#C9A84C',
  violet:   '#E8B96A',
  pink:     '#EC4899',
  cyan:     '#06B6D4',
  amber:    '#C9A84C',
  sigAmber: '#F59E0B',
  sigRed:   '#FF4444',
  sigGreen: '#10B981',
  muted:    'rgba(255,255,255,0.48)',
  surface:  '#1A1A2E',
  surface2: '#22223E',
  // ── Retained pre-existing keys (used elsewhere in this file) ──
  card:   '#111B2E',
  border: 'rgba(255,255,255,0.07)',
  white:  '#F2F6FA',
  fog:    '#6B7E91',
  green:  '#2EC8A0',
  red:    '#FF3B5C',
};
const NAV_H = 60;

/* ================================================================
   TOOL DATABASE (Finance)
   ================================================================ */
const TOOLS = [
  // Finance AI
  { id: 'workiva', name: 'Workiva', category: 'Finance AI', description: 'Automated financial reporting and compliance documentation', tasks_addressed: ['compliance reporting', 'audit documentation', 'regulatory filings', 'financial statement preparation'], logo_color: '#0066CC' },
  { id: 'coupa', name: 'Coupa', category: 'Finance AI', description: 'AI-powered spend management and procurement automation', tasks_addressed: ['invoice processing', 'purchase order management', 'spend analysis', 'vendor management'], logo_color: '#E31837' },
  { id: 'blackline', name: 'BlackLine', category: 'Finance AI', description: 'Continuous accounting and financial close automation', tasks_addressed: ['bank reconciliation', 'journal entry', 'account reconciliation', 'close management'], logo_color: '#00A651' },
  { id: 'kyriba', name: 'Kyriba', category: 'Finance AI', description: 'Treasury and liquidity management automation', tasks_addressed: ['cash positioning', 'liquidity forecasting', 'fx risk management', 'bank connectivity'], logo_color: '#6B2D8B' },
  { id: 'sage', name: 'Sage Intacct AI', category: 'Finance AI', description: 'AI-enhanced accounts and financial operations', tasks_addressed: ['accounts payable', 'accounts receivable', 'expense management', 'financial reporting'], logo_color: '#00DC82' },
  // Analytics AI
  { id: 'anaplan', name: 'Anaplan', category: 'Analytics AI', description: 'Connected planning and performance management', tasks_addressed: ['financial forecasting', 'budget planning', 'variance analysis', 'kpi reporting'], logo_color: '#0082C8' },
  { id: 'palantir', name: 'Palantir Foundry', category: 'Analytics AI', description: 'Enterprise data integration and analytics automation', tasks_addressed: ['data reconciliation', 'management reporting', 'data extraction', 'performance analytics'], logo_color: '#1A1A2E' },
  // Compliance AI
  { id: 'servicenow_fin', name: 'ServiceNow Finance', category: 'Compliance AI', description: 'Automated compliance workflows and risk management', tasks_addressed: ['compliance monitoring', 'risk assessment', 'audit management', 'policy adherence'], logo_color: '#81B5A1' },
  { id: 'tr', name: 'Thomson Reuters ONESOURCE', category: 'Compliance AI', description: 'Tax compliance and regulatory reporting automation', tasks_addressed: ['tax compliance', 'regulatory reporting', 'transfer pricing', 'statutory filings'], logo_color: '#FF8000' },
  // HR AI
  { id: 'workday', name: 'Workday AI', category: 'HR AI', description: 'AI-powered HR operations, payroll automation, and workforce analytics.', tasks_addressed: ['manage payroll', 'track attendance', 'process hr requests', 'workforce reporting'], logo_color: '#E87722' },
  { id: 'lattice', name: 'Lattice', category: 'HR AI', description: 'Performance management and employee engagement automation.', tasks_addressed: ['performance reviews', 'employee feedback', 'goal setting', 'engagement surveys'], logo_color: '#6C5CE7' },
  { id: 'leena', name: 'Leena AI', category: 'HR AI', description: 'HR helpdesk automation and employee query resolution.', tasks_addressed: ['answer hr queries', 'onboarding automation', 'policy compliance', 'leave management'], logo_color: '#00B4D8' },
  // Marketing AI
  { id: 'jasper', name: 'Jasper AI', category: 'Marketing AI', description: 'AI content generation for marketing campaigns and brand communications.', tasks_addressed: ['create marketing content', 'manage brand communications', 'draft campaign copy', 'content strategy'], logo_color: '#F97316' },
  { id: 'hubspot_ai', name: 'HubSpot AI', category: 'Marketing AI', description: 'AI-driven CRM, lead scoring, and marketing automation.', tasks_addressed: ['manage leads', 'track marketing performance', 'monitor brand perception', 'customer segmentation'], logo_color: '#FF7A59' },
  { id: 'brandwatch', name: 'Brandwatch', category: 'Marketing AI', description: 'Brand monitoring, social listening, and market intelligence automation.', tasks_addressed: ['monitor brand perception', 'social media monitoring', 'competitive analysis', 'market research'], logo_color: '#1DA1F2' },
  // Operations AI
  { id: 'uipath', name: 'UiPath', category: 'Operations AI', description: 'Robotic process automation for repetitive operational workflows.', tasks_addressed: ['process transactions', 'data entry automation', 'workflow automation', 'reporting automation'], logo_color: '#FA4616' },
  { id: 'servicenow_ops', name: 'ServiceNow AI', category: 'Operations AI', description: 'IT and operations workflow automation and service management.', tasks_addressed: ['manage service requests', 'incident management', 'process approvals', 'operational reporting'], logo_color: '#62D84E' },
  { id: 'celonis', name: 'Celonis', category: 'Operations AI', description: 'Process mining and operational intelligence for workflow optimisation.', tasks_addressed: ['analyse processes', 'identify bottlenecks', 'monitor operational efficiency', 'process improvement'], logo_color: '#00B4A0' },
  // Technology AI
  { id: 'github_copilot', name: 'GitHub Copilot', category: 'Technology AI', description: 'AI pair programmer for code generation, review and documentation.', tasks_addressed: ['write code', 'code review', 'software documentation', 'technical development'], logo_color: '#24292E' },
  { id: 'datarobot', name: 'DataRobot', category: 'Technology AI', description: 'Automated machine learning and AI model deployment platform.', tasks_addressed: ['build predictive models', 'data analysis', 'develop forecasting models', 'analytics automation'], logo_color: '#00A3E0' },
  { id: 'snowflake', name: 'Snowflake Cortex', category: 'Technology AI', description: 'AI-powered data platform for analytics and business intelligence.', tasks_addressed: ['data management', 'conduct performance analytics', 'business intelligence', 'data reporting'], logo_color: '#29B5E8' },
  // Legal AI
  { id: 'harvey', name: 'Harvey AI', category: 'Legal AI', description: 'AI legal research, contract review and compliance documentation.', tasks_addressed: ['contract review', 'legal research', 'compliance documentation', 'regulatory reporting'], logo_color: '#1A1A2E' },
  { id: 'luminance', name: 'Luminance', category: 'Legal AI', description: 'AI-powered contract analysis and due diligence automation.', tasks_addressed: ['review contracts', 'due diligence', 'document analysis', 'legal compliance'], logo_color: '#7B2D8B' },
  // Sales AI
  { id: 'salesforce_einstein', name: 'Salesforce Einstein', category: 'Sales AI', description: 'AI-powered CRM, sales forecasting and customer relationship automation.', tasks_addressed: ['manage customer relationships', 'sales forecasting', 'lead management', 'customer reporting'], logo_color: '#00A1E0' },
  { id: 'gong', name: 'Gong AI', category: 'Sales AI', description: 'Revenue intelligence and sales conversation analytics.', tasks_addressed: ['analyse sales calls', 'track sales performance', 'customer engagement', 'pipeline management'], logo_color: '#6366F1' },
];

/* ================================================================
   TOP NAV
   ================================================================ */
const TopNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const dailyNav = [
    { label: 'Signal Room', icon: Radio, path: '/signal-room' },
    { label: 'Intelligence', icon: BarChart2, path: '/intelligence' },
    { label: 'Blueprint Room', icon: LayoutGrid, path: '/blueprint/cockpit' },
    { label: 'The Stage', icon: Users, path: '/stage' },
    { label: 'Showcase', icon: Sparkles, path: '/showcase/ai-tools' },
  ];
  const flowNav = [
    { label: 'Deconstruct', icon: Layers, path: '/deconstruct' },
    { label: 'Analyze', icon: LineChart, path: '/analyze' },
    { label: 'Sort · 4B', icon: Shuffle, path: '/sort' },
    { label: 'Recompose', icon: GitMerge, path: '/recompose' },
  ];
  const renderNavLink = (item) => {
    const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    return (
      <Link key={item.path} to={item.path}
        className={cn("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
          active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        )}>
        <item.icon className="h-4 w-4" />{item.label}
      </Link>
    );
  };
  return (
    <header style={{ height: NAV_H, position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, background: 'rgba(3,5,10,0.85)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', height: '100%', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <Link to="/signal-room"><Logo size="nav" /></Link>
          <nav style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {dailyNav.map(renderNavLink)}
            <span aria-hidden="true" style={{ display: 'inline-block', width: 1, height: 18, background: 'var(--line)', margin: '0 8px' }} />
            {flowNav.map(renderNavLink)}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,212,200,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.teal }}>{user?.name?.charAt(0) || 'C'}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="text-muted-foreground">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
const ShowcaseAI = () => {
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [shortlisted, setShortlisted] = useState([]);
  const [scored, setScored] = useState([]);

  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  useEffect(() => {
    const raw = localStorage.getItem('reai_showcase_context');
    if (raw) {
      try { setContext(JSON.parse(raw)); } catch { /* ignore */ }
    }
    const saved = localStorage.getItem('reai_showcase_shortlist');
    if (saved) {
      try { setShortlisted(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  const roleParam = new URLSearchParams(window.location.search).get('role');

  // Compute match scores with hard category filtering by function domain
  useEffect(() => {
    if (!context) return;
    const rawThemes = (context.bot_task_themes || []).map((t) => t.toLowerCase());
    const themeWords = rawThemes.flatMap((t) => t.split(/\s+/).filter((w) => w.length >= 3));

    const rf = (context.role_filter || context.function || '').toLowerCase();

    // Detect function domain from role/function string
    let domain = null;
    if (/\b(hr|people|human\s*resource|talent|employee|compensation|benefits|culture)\b/.test(rf)) domain = 'hr';
    else if (/\b(finance|controller|cfo|accounting|treasury|audit)\b/.test(rf)) domain = 'finance';
    else if (/marketing|brand|content|communications/.test(rf)) domain = 'marketing';
    else if (/\b(tech|digital|engineering|it|software|data)\b/.test(rf)) domain = 'tech';
    else if (/\b(operations|supply|procurement|logistics|manufacturing)\b/.test(rf)) domain = 'operations';
    else if (/\b(legal|compliance|regulatory|risk)\b/.test(rf)) domain = 'legal';
    else if (/\b(sales|customer|revenue|commercial|account)\b/.test(rf)) domain = 'sales';

    const DOMAIN_CATEGORIES = {
      hr:         { primary: ['HR AI'],                        cross: ['Analytics AI', 'Operations AI'],                  exclude: ['Finance AI', 'Compliance AI', 'Legal AI', 'Sales AI', 'Marketing AI', 'Technology AI'] },
      finance:    { primary: ['Finance AI', 'Analytics AI', 'Compliance AI'], cross: ['Operations AI', 'Technology AI'],         exclude: ['HR AI', 'Marketing AI', 'Sales AI', 'Legal AI'] },
      marketing:  { primary: ['Marketing AI'],                 cross: ['Analytics AI', 'Sales AI'],                       exclude: ['Finance AI', 'HR AI', 'Legal AI', 'Compliance AI'] },
      tech:       { primary: ['Technology AI'],                cross: ['Analytics AI', 'Operations AI'],                  exclude: ['HR AI', 'Marketing AI', 'Sales AI'] },
      operations: { primary: ['Operations AI'],                cross: ['Analytics AI', 'Technology AI'],                  exclude: ['HR AI', 'Marketing AI', 'Legal AI'] },
      legal:      { primary: ['Legal AI', 'Compliance AI'],    cross: ['Analytics AI'],                                   exclude: ['HR AI', 'Marketing AI', 'Sales AI'] },
      sales:      { primary: ['Sales AI'],                     cross: ['Marketing AI', 'Analytics AI'],                   exclude: ['Finance AI', 'HR AI', 'Legal AI', 'Compliance AI'] },
    };
    // Per-domain tool id block-list — catches tools whose category is misleading
    // (e.g. GitHub Copilot is tagged 'Technology AI' but is a pure developer tool
    // and should never surface for HR, Finance, Marketing, etc.)
    const DOMAIN_TOOL_BLOCKLIST = {
      hr:         ['github_copilot', 'datarobot', 'snowflake', 'harvey', 'luminance'],
      finance:    ['github_copilot', 'jasper', 'gong'],
      marketing:  ['github_copilot', 'datarobot', 'snowflake', 'harvey', 'luminance'],
      operations: ['github_copilot', 'jasper', 'harvey', 'luminance'],
      legal:      ['github_copilot', 'jasper', 'gong', 'datarobot'],
      sales:      ['github_copilot', 'harvey', 'luminance'],
      tech:       [],
    };

    const catConfig = domain ? DOMAIN_CATEGORIES[domain] : null;
    const primary = new Set(catConfig?.primary || []);
    const cross = new Set(catConfig?.cross || []);
    const excluded = new Set(catConfig?.exclude || []);
    const blockedIds = new Set((domain && DOMAIN_TOOL_BLOCKLIST[domain]) || []);

    // Score every tool first
    const allScored = TOOLS.map((tool) => {
      const matchedTasks = tool.tasks_addressed.filter((ta) =>
        rawThemes.some((theme) => ta.includes(theme) || theme.includes(ta)) ||
        themeWords.some((word) => ta.toLowerCase().includes(word))
      );
      const baseScore = tool.tasks_addressed.length > 0
        ? Math.round(matchedTasks.length / tool.tasks_addressed.length * 100)
        : 0;
      return { ...tool, baseScore, matchedTasks };
    });

    // Filter: when a domain is detected, apply hard category rules + tool blocklist
    let filtered;
    if (catConfig) {
      const primaryTools = allScored.filter((t) => primary.has(t.category) && !blockedIds.has(t.id));
      const needPad = primaryTools.length < 6;
      const crossTools = allScored.filter((t) => {
        if (blockedIds.has(t.id)) return false;
        if (primary.has(t.category)) return false;
        if (excluded.has(t.category)) return false;
        // Non-primary, non-excluded: include only if real task match > 0
        // OR if we need to pad to 6 and it's a cross-functional category
        if (t.baseScore > 0) return true;
        if (needPad && cross.has(t.category)) return true;
        return false;
      });
      filtered = [...primaryTools, ...crossTools];
    } else {
      filtered = allScored;
    }

    // Apply match score: primary tools get +25 boost so they land with a meaningful badge
    const results = filtered.map((t) => {
      const boost = primary.has(t.category) ? 25 : (cross.has(t.category) ? 10 : 0);
      return { ...t, matchScore: Math.min(t.baseScore + boost, 100) };
    });

    // Sort: primary first (by score desc), then cross, then others
    const rank = (t) => primary.has(t.category) ? 0 : (cross.has(t.category) ? 1 : 2);
    results.sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      return b.matchScore - a.matchScore;
    });
    setScored(results);
  }, [context]);

  const toggleShortlist = (toolId) => {
    setShortlisted((prev) => {
      const next = prev.includes(toolId) ? prev.filter((id) => id !== toolId) : [...prev, toolId];
      localStorage.setItem('reai_showcase_shortlist', JSON.stringify(next));
      return next;
    });
  };

  const tasksCovered = new Set();
  scored.filter((t) => shortlisted.includes(t.id)).forEach((t) => {
    t.matchedTasks.forEach((mt) => tasksCovered.add(mt));
  });

  const matchBadge = (score) => {
    if (score > 60) return { label: 'Strong match', color: C.green };
    if (score >= 30) return { label: 'Good match', color: C.amber };
    return { label: 'Partial match', color: C.fog };
  };

  const shortlistedTools = scored.filter((t) => shortlisted.includes(t.id));
  const otherTools = scored.filter((t) => !shortlisted.includes(t.id));

  return (
    <div style={{ background: C.bg, color: C.white, fontFamily: "'Inter', sans-serif", minHeight: '100vh' }}>
      <TopNav />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: `${NAV_H + 40}px 24px 120px` }}>

        {/* Header */}
        <button onClick={() => { const returnUrl = localStorage.getItem('showcase_return_url') || '/blueprint/cockpit'; localStorage.removeItem('showcase_return_url'); navigate(returnUrl); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: C.muted, padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          Back
        </button>

        <h1 style={{ fontFamily: 'var(--fg)', fontSize: 'clamp(28px, 4vw, 42px)', color: C.white, letterSpacing: '0.02em', marginBottom: 6 }}>
          {roleParam
            ? `AI Solutions for ${roleParam}`
            : `AI Solutions for your ${context?.function || ''} transformation`
          }
        </h1>
        <p style={{ fontFamily: 'var(--fm)', fontSize: 12, color: C.muted, marginBottom: 24 }}>
          {context?.role_filter
            ? `${context?.bot_task_count || 0} BOT tasks identified for ${context.role_filter}`
            : `${context?.bot_task_count || 0} BOT tasks identified across ${context?.roles?.length || 0} roles · ${context?.function || 'Finance'} function`
          }
        </p>

        {/* Context banner */}
        {context && (
          <div style={{ background: `${C.teal}10`, border: `1px solid ${C.teal}25`, borderRadius: 12, padding: '16px 20px', marginBottom: 32 }}>
            <p style={{ fontSize: 13, color: C.teal, lineHeight: 1.5 }}>
              Based on your analysis: <strong>{context.bot_task_count}</strong> tasks across <strong>{context.roles?.length}</strong> roles are AI-addressable. These tools are matched to your specific task clusters.
            </p>
            {context.bot_task_themes?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {context.bot_task_themes.slice(0, 8).map((theme, i) => (
                  <span key={i} style={{ fontFamily: 'var(--fm)', fontSize: 10, padding: '3px 8px', borderRadius: 4, background: `${C.violet}15`, color: C.violet }}>
                    {theme}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Shortlisted section */}
        {shortlistedTools.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <p style={{ fontFamily: 'var(--fm)', fontSize: 11, color: C.teal, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
              Shortlisted ({shortlistedTools.length})
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
              {shortlistedTools.map((tool) => renderToolCard(tool, true))}
            </div>
          </div>
        )}

        {/* All tools */}
        <p style={{ fontFamily: 'var(--fm)', fontSize: 11, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
          {shortlistedTools.length > 0 ? 'Other tools' : 'Matched tools'} ({otherTools.length})
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {otherTools.map((tool) => renderToolCard(tool, false))}
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: C.surface, borderTop: `1px solid ${C.border}`, padding: '14px 24px', zIndex: 100 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--fm)', fontSize: 12, color: C.muted }}>
            <strong style={{ color: C.violet }}>{shortlisted.length}</strong> tools shortlisted &middot; <strong style={{ color: C.teal }}>{tasksCovered.size}</strong> task themes covered
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { toast.success('Shortlist exported'); }} style={{ padding: '10px 20px', fontSize: 13, fontWeight: 500, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: 'pointer' }}>
              Export Shortlist
            </button>
            <button onClick={() => navigate('/blueprint/cockpit')} style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, background: C.gold, border: 'none', borderRadius: 8, color: C.bg, cursor: 'pointer' }}>
              Complete Blueprint &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  function renderToolCard(tool, isShortlisted) {
    const badge = matchBadge(tool.matchScore);
    return (
      <div key={tool.id} style={{
        background: C.card, border: `1px solid ${isShortlisted ? C.teal : C.border}`,
        borderRadius: 12, padding: 20, transition: 'border-color 0.2s',
        display: 'flex', flexDirection: 'column', minHeight: 320,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, background: tool.logo_color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fg)', fontSize: 16, color: 'var(--text)', flexShrink: 0 }}>
            {tool.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: C.white }}>{tool.name}</p>
            <span style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.muted }}>{tool.category}</span>
          </div>
          <span style={{ fontFamily: 'var(--fm)', fontSize: 10, padding: '3px 8px', borderRadius: 4, background: `${badge.color}18`, color: badge.color }}>
            {badge.label} · {tool.matchScore}%
          </span>
        </div>

        {/* Description */}
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>{tool.description}</p>

        {/* Matched tasks */}
        <div style={{ flex: 1, marginBottom: 12 }}>
          {tool.matchedTasks.length > 0 ? (
            <div>
              <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.fog, marginBottom: 6 }}>Addresses:</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {tool.matchedTasks.map((mt, i) => (
                  <span key={i} style={{ fontFamily: 'var(--fm)', fontSize: 9, padding: '2px 6px', borderRadius: 4, background: `${C.violet}12`, color: C.violet }}>
                    {mt}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.fog }}>No direct task matches</p>
          )}
        </div>

        {/* Actions - pinned to bottom */}
        <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
          <button onClick={() => toggleShortlist(tool.id)} style={{
            flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
            background: isShortlisted ? C.teal : 'transparent',
            color: isShortlisted ? C.bg : C.muted,
            border: isShortlisted ? 'none' : `1px solid ${C.border}`,
          }}>
            {isShortlisted ? 'Shortlisted \u2713' : 'Shortlist'}
          </button>
          <button onClick={() => toast.info(`${tool.name} details coming soon`)} style={{
            flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6, cursor: 'pointer',
            background: 'transparent', border: `1px solid ${C.border}`, color: C.muted,
          }}>
            Learn More
          </button>
          <button onClick={() => toast.info('Skipped')} style={{
            padding: '8px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6, cursor: 'pointer',
            background: 'transparent', border: `1px solid ${C.border}`, color: C.fog,
          }}>
            Skip
          </button>
        </div>
      </div>
    );
  }
};

const ShowcaseAIWithExplainer = (props) => (<><ShowcaseAI {...props} /><DemoExplainer screen="SHOWCASE" /></>);
export default ShowcaseAIWithExplainer;
